import React from 'react';
import styled, { css } from 'styled-components';
import { MobileAppFlair } from 'types/graphql';
import { AddButton } from 'src/dispensary-admin/components/product-card/mobile-app-product-card';
import SvgAddIcon from 'src/dispensary-admin/customize/components/preview/home-page/carousels/product-carousel/product-carousel-card/svg/add-icon';
import { FLAIR_VIEWBOX, getFlairPath } from './preview/home-page/get-flair-path';

type ButtonFlairPreviewProps = {
  flairType: MobileAppFlair;
  color: string;
};

const TOP_BOTTOM_SPIKE_COUNT = 50;
const SIDE_SPIKE_COUNT = 5;
/** How far spikes reach past the body, per axis, sized so every spike is ~4.5px on a 300x36 button. */
const SPIKE_WIDTH = 1.5;
const SPIKE_HEIGHT = 12.5;

const percent = (value: number): string => `${Number(value.toFixed(2))}%`;

/**
 * Points for one spiked edge of the button body, from its starting corner up to (but excluding) the
 * next corner. Even indices sit on the body edge and odd indices are spike tips, so each edge both
 * starts and ends flush with the body -- leaving the corners as clean right angles that the spikes
 * of the two adjoining edges poke past, rather than corners that jut out on their own.
 */
const spikedEdge = (
  count: number,
  from: number,
  to: number,
  point: (along: number, isTip: boolean) => string
): string[] =>
  Array.from({ length: count * 2 }, (_, index) =>
    point(from + ((to - from) * index) / (count * 2), Boolean(index % 2))
  );

export const JAGGED_BUTTON_CLIP_PATH = `polygon(${[
  ...spikedEdge(
    TOP_BOTTOM_SPIKE_COUNT,
    SPIKE_WIDTH,
    100 - SPIKE_WIDTH,
    (x, isTip) => `${percent(x)} ${percent(isTip ? 0 : SPIKE_HEIGHT)}`
  ),
  ...spikedEdge(
    SIDE_SPIKE_COUNT,
    SPIKE_HEIGHT,
    100 - SPIKE_HEIGHT,
    (y, isTip) => `${percent(isTip ? 100 : 100 - SPIKE_WIDTH)} ${percent(y)}`
  ),
  ...spikedEdge(
    TOP_BOTTOM_SPIKE_COUNT,
    100 - SPIKE_WIDTH,
    SPIKE_WIDTH,
    (x, isTip) => `${percent(x)} ${percent(isTip ? 100 : 100 - SPIKE_HEIGHT)}`
  ),
  ...spikedEdge(
    SIDE_SPIKE_COUNT,
    100 - SPIKE_HEIGHT,
    SPIKE_HEIGHT,
    (y, isTip) => `${percent(isTip ? 0 : SPIKE_WIDTH)} ${percent(y)}`
  ),
].join(', ')})`;

export function getTextButtonClipPath(flair: MobileAppFlair): string {
  switch (flair) {
    case MobileAppFlair.angled:
      return 'polygon(14px 0, 100% 0, calc(100% - 14px) 100%, 0 100%)';
    case MobileAppFlair.jagged:
      return JAGGED_BUTTON_CLIP_PATH;
    case MobileAppFlair.curved:
    case MobileAppFlair.none:
    case MobileAppFlair.wavy:
    case MobileAppFlair.checkerBoard:
    default:
      return 'none';
  }
}

/**
 * Renders a preview of mobile app buttons for the selected decoration type.
 * Shows a text button decorated along its edges (or shaped into a trapezoid or jagged outline),
 * alongside the square/circular icon Add to Cart button shaped by the clip path.
 */
export function ButtonFlairPreview({ flairType, color }: ButtonFlairPreviewProps): JSX.Element {
  const path = getFlairPath(flairType);
  // Checkerboard has no button treatment either, same as curved/angled/jagged above -- it's a
  // pattern fill that doesn't read well at button scale (see getButtonClipPath below).
  const hasTopBottomFlair =
    flairType !== MobileAppFlair.none &&
    flairType !== MobileAppFlair.angled &&
    flairType !== MobileAppFlair.jagged &&
    flairType !== MobileAppFlair.curved &&
    flairType !== MobileAppFlair.checkerBoard &&
    Boolean(path);

  return (
    <Container aria-hidden='true'>
      <TextButtonWrapper>
        {hasTopBottomFlair && (
          <FlairEdge>
            <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
              <path d={path} fill={color} />
            </svg>
          </FlairEdge>
        )}
        <TextButtonContent $color={color} $hasFlair={hasTopBottomFlair} $flairType={flairType}>
          <ButtonText>Preview</ButtonText>
        </TextButtonContent>
        {hasTopBottomFlair && (
          <FlairEdge $flipped>
            <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
              <path d={path} fill={color} />
            </svg>
          </FlairEdge>
        )}
      </TextButtonWrapper>

      <AddButton type='button' aria-label='Add to cart' $buttonFlair={flairType} $color={color}>
        <SvgAddIcon />
      </AddButton>
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 8px;
  width: 100%;
  max-width: 300px;
`;

const TextButtonWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

const TextButtonContent = styled.div<{ $color: string; $hasFlair: boolean; $flairType: MobileAppFlair }>`
  align-items: center;
  background: ${({ $color }) => $color || 'transparent'};
  border-radius: ${({ $hasFlair, $flairType }) =>
    $hasFlair || $flairType === MobileAppFlair.angled || $flairType === MobileAppFlair.jagged ? '0' : '4px'};
  clip-path: ${({ $flairType }) => getTextButtonClipPath($flairType)};
  display: flex;
  height: ${({ $hasFlair }) => ($hasFlair ? '32px' : '36px')};
  justify-content: center;
  padding: 0 16px;
  width: 100%;
`;

const ButtonText = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
`;

const FlairEdge = styled.div<{ $flipped?: boolean }>`
  height: 4px;
  line-height: 0;
  width: 100%;

  ${({ $flipped }) =>
    $flipped &&
    css`
      transform: scaleY(-1);
    `}

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`;
